import { useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../../../../components/ui/molecules/table';
import { ChartCard } from '../components';
import { TrendChartProps, TrendDatum } from '../interface';
import { TREND_METRICS, trendData, trendIsEmpty, formatRangeLabel } from '../utils';

const AXIS_TICK = { fill: 'hsl(var(--muted-foreground))', fontSize: 12 };
const GRID = 'hsl(var(--border))';
const SURFACE = 'hsl(var(--card))';

interface TooltipProps {
  active?: boolean;
  payload?: { payload: TrendDatum }[];
  name: string;
  color: string;
  hoursLabel: string;
}

function TrendTooltip({ active, payload, name, color, hoursLabel }: TooltipProps) {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;
  return (
    <div className="rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md">
      <p className="font-medium">{formatRangeLabel(point.from, point.to)}</p>
      <p className="text-muted-foreground">{point.workingDays} working {point.workingDays === 1 ? 'day' : 'days'}</p>
      <p className="mt-1 flex items-center gap-1.5">
        <span aria-hidden="true" className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: color }} />
        <span>{name}</span>
        <span className="ml-auto pl-3 font-medium">{point.utilization === null ? 'No data' : `${point.utilization}%`}</span>
      </p>
      {point.utilization !== null && (
        <p className="text-muted-foreground">{point.hours}h {hoursLabel} of {point.availableHours}h available</p>
      )}
    </div>
  );
}

export function TrendChart({ data, isLoading, isError, metric, yMax }: TrendChartProps) {
  const def = TREND_METRICS[metric];
  const points = useMemo(() => (data ? trendData(data, metric) : []), [data, metric]);
  const lastWithValue = useMemo(() => {
    for (let i = points.length - 1; i >= 0; i--) if (points[i].utilization !== null) return i;
    return -1;
  }, [points]);

  return (
    <ChartCard
      title={def.title}
      subtitle={def.subtitle}
      isLoading={isLoading}
      isError={isError}
      isEmpty={!!data && (data.points.length === 0 || trendIsEmpty(data, metric))}
      emptyMessage="No capacity to measure in this range."
      chart={(
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 16, right: 24, bottom: 4, left: 0 }}>
              <CartesianGrid vertical={false} stroke={GRID} />
              <XAxis
                dataKey="label"
                tick={AXIS_TICK}
                tickLine={false}
                axisLine={{ stroke: GRID }}
                interval="preserveStartEnd"
                minTickGap={16}
              />
              <YAxis
                domain={[0, yMax]}
                tickFormatter={(v: number) => `${v}%`}
                tick={AXIS_TICK}
                tickLine={false}
                axisLine={false}
                width={44}
              />
              <Tooltip
                cursor={{ stroke: GRID }}
                content={<TrendTooltip name={def.title.replace(' over time', '')} color={def.color} hoursLabel={def.hoursLabel} />}
              />
              <Line
                dataKey="utilization"
                type="linear"
                stroke={def.color}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                connectNulls={false}
                isAnimationActive={false}
                dot={{ r: 4, fill: def.color, stroke: SURFACE, strokeWidth: 2 }}
                activeDot={{ r: 6, fill: def.color, stroke: SURFACE, strokeWidth: 2 }}
                label={({ x, y, value, index }: { x?: number | string; y?: number | string; value?: unknown; index?: number }) => (
                  // Only the last point is labelled: the axis and tooltip carry the rest.
                  index === lastWithValue && typeof value === 'number'
                    ? (
                      <text x={Number(x)} y={Number(y) - 12} textAnchor="middle" fontSize={12} fontWeight={600} fill="hsl(var(--foreground))">
                        {value}%
                      </text>
                    )
                    : <g key={index} />
                )}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      table={(
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Period</TableHead>
              <TableHead>Working days</TableHead>
              <TableHead>Hours ({def.hoursLabel})</TableHead>
              <TableHead>Available hours</TableHead>
              <TableHead>Utilization</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {points.map((p) => (
              <TableRow key={p.from}>
                <TableCell>{formatRangeLabel(p.from, p.to)}</TableCell>
                <TableCell>{p.workingDays}</TableCell>
                <TableCell>{p.hours}h</TableCell>
                <TableCell>{p.availableHours}h</TableCell>
                <TableCell>{p.utilization === null ? 'No data' : `${p.utilization}%`}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    />
  );
}
